import { GetApiPayloadByUrl, GetApiResponseByUrl } from '@crossbeam/openapi';

import { IncomingDataShare, OutgoingDataShare } from './data_shares';
import type { Feed } from './feeds';
import { Partner } from './partners';
import type { Source } from './sources';

export const CUSTOMERS_STANDARD_TYPE =
  'customers' satisfies StandardPopulationType;
export const OPEN_OPPS_STANDARD_TYPE =
  'open_opportunities' satisfies StandardPopulationType;
export const PROSPECTS_STANDARD_TYPE =
  'prospects' satisfies StandardPopulationType;
export const OTHER = 'other' satisfies SegmentType;

export const STANDARD_POPULATIONS = [
  'customers',
  'open_opportunities',
  'prospects',
] as const satisfies readonly StandardPopulationType[];

export const ALL_SEGMENTS = [
  'customers',
  'open_opportunities',
  'prospects',
  'other',
] as const satisfies readonly SegmentType[];

export type StandardPopulationType = NonNullable<Population['standard_type']>;

export const SEGMENTS_TO_LABEL_MAP = {
  customers: 'Customers',
  open_opportunities: 'Open Opportunities',
  prospects: 'Prospects',
  other: 'Other',
} as const satisfies Record<SegmentType, string>;

export type SegmentType = NonNullable<Population['segment']>;

export type OtherPopulationType = typeof OTHER;

export type PopulationStats =
  GetApiResponseByUrl<'/alpha/population-stats'>['items'][number];

type Primitive = string | number | boolean | null;
export type Operand = Primitive | Primitive[];
export type Combinator = 'AND' | 'OR';
export type Population =
  GetApiResponseByUrl<'/v0.3/populations'>['items'][number];
export type FilterGroup = NonNullable<
  Population['source_filter_expressions']
>[string];
export type FilterPart = NonNullable<FilterGroup['filter_parts']>[number];
export type FilterExpression = FilterGroup['filter_expression'];
export type FilterOperator = FilterPart['operator'];

export type PopulationPostPayload = GetApiPayloadByUrl<
  '/v0.3/populations',
  'post'
>;
export type PopulationPatchPayload = GetApiPayloadByUrl<
  '/v0.3/populations/{id}',
  'patch'
>;

export type PopulationPreviewPayload = GetApiPayloadByUrl<
  '/v0.3/population-preview',
  'post'
>;

export type PostPopulationResponse = GetApiResponseByUrl<
  '/v0.3/populations',
  'post'
>;

export type EmptyPopulation = Partial<Population>;

type PopulationLike =
  | Record<string, unknown>
  | Population
  | EmptyPopulation
  | null
  | undefined;

export function isOpenDataPopulation(population: PopulationLike): boolean {
  return (
    !!population && 'origin' in population && population.origin === 'system'
  );
}

export interface InternalFilterPart {
  label?: string;
  operand?: unknown;
  operator?: string;
  source_field_id?: number;
  id?: number;
}

export interface InternalFilterGroup {
  filter_expression: FilterExpression;
  filter_parts: InternalFilterPart[];
  tableName?: string;
  mdmType?: string;
}

export type PartnerPopulation =
  GetApiResponseByUrl<'/v0.1/partner-populations'>['items'][number];

export type PopulationToPopulationSetting = {
  dataShare: IncomingDataShare | OutgoingDataShare;
  population: Population;
  partnerOrganization: Partner;
  sourceFields: IncomingDataShare['shared_fields'];
};

export type PopPreviewResponse = GetApiResponseByUrl<
  '/v0.3/population-preview',
  'post'
>;
export type PopPreviewFieldItem = NonNullable<
  PopPreviewResponse['fields']
>[number];
export type PopPreviewRowItem = NonNullable<PopPreviewResponse['rows']>[number];

export type VisibilityText = {
  title: string;
  value: string;
  cssClasses?: string[];
  variantClasses?: string[];
  subtitle: (params?: {
    partnerName?: string;
    populationName?: string;
    showSubject?: boolean;
    isTerse?: boolean;
  }) => string;
};

export type SuggestedFiltersResponse =
  GetApiResponseByUrl<'/v0.1/populations-suggest-filter'>;
export type RecommendedFiltersResponse =
  SuggestedFiltersResponse['recommended_filters'];
export type TextualSuggestionResponse =
  SuggestedFiltersResponse['textual_suggestion'];

export type MatchesExportPayload = GetApiPayloadByUrl<
  '/v0.3/matches/export',
  'post'
>;

export type PopulationFilterConfig = {
  filterGroupList?: InternalFilterGroup[];
  completeFilterSources?: Source[];
  hasInvalidFilters?: boolean;
  hasFiltersChanged?: boolean;
  offlinePartnerUuid?: string;
};

export type PopulationAlertConfig = {
  populationFiltersAlertVisibility?: boolean;
  populationFiltersAlertContent?: {
    message: string;
    description?: string;
  } | null;
};

export type PopulationDataSourceConfig = {
  currentFeeds: Feed[];
  currentSources: Source[];
  currentUploadNames: string[];
};

export type PopulationFormConfig = {
  isCustomPopulation: boolean;
  isEdit: boolean;
  isOfflinePartner: boolean;
  isOpenData?: boolean;
  isSourceLocked?: boolean;
  isViewMode?: boolean;
};
